import * as React from 'react'
import {
  Bot,
  ChevronDown,
  Cpu,
  Database,
  File,
  Files,
  Loader2,
  Network,
  Radio,
  Server,
  Tags,
  Trash2
} from 'lucide-react'
import {
  fetchSiteStoredDocument,
  useDeleteIngestedFileMutation,
  useListSiteDocumentsQuery,
  useListSiteUnstructuredFilesQuery,
  type DeleteIngestedFileArg,
  type SiteDocument,
  type SiteDocumentKind,
  type SiteUnstructuredFile
} from '@axilon-apps/domains-ontology-ui-state'
import { IngestFileDeleteDialog } from '../../../components/IngestFileDeleteDialog'
import { SourceDocViewer } from '../../../components/SourceDocViewer/SourceDocViewer'
import { useSourceDocViewer } from '../../../components/SourceDocViewer/useSourceDocViewer'
import { Card } from '../../../components/ui/card'
import { StatusPill } from '../../../components/primitives/StatusPill'
import { useDebugMode } from '../../../lib/debug-mode'
import type { Status } from '../../../components/primitives/types'
import { cn } from '../../../lib/utils'
import { formatRelativeTime } from '../../../lib/time'
import { formatBytes } from '../../DocsHubPage/doc-helpers'
import { alternateSiteStorageIds } from '../wizard/siteFilter'

export type IngestedFileGroupId =
  | 'scada'
  | 'plc'
  | 'ipmap'
  | 'opctags'
  | 'robotics'
  | 'mtconnect'
  | 'erp'
  | 'unstructured'

interface FileGroupSpec {
  id: IngestedFileGroupId
  path: string
  desc: string
  icon: React.ComponentType<{ className?: string }>
  /** Proxy list `kind` that fills this group; absent ⇒ filled elsewhere (or empty). */
  storageKind?: SiteDocumentKind
}

const FILE_GROUPS: FileGroupSpec[] = [
  {
    id: 'scada',
    path: 'SCADA/',
    desc: 'Gateway backups and exported tag providers',
    icon: Server,
    storageKind: 'project-roots'
  },
  {
    id: 'plc',
    path: 'PLC/',
    desc: 'Controller programs and routine exports',
    icon: Cpu,
    storageKind: 'plc'
  },
  {
    id: 'ipmap',
    path: 'IP-map/',
    desc: 'Device and OPC server to controller mappings',
    icon: Network,
    storageKind: 'ip-map'
  },
  {
    id: 'opctags',
    path: 'Tags/',
    desc: 'Curated OPC tag lists registered via tag import',
    icon: Tags,
    storageKind: 'tag-lists'
  },
  {
    id: 'robotics',
    path: 'Robots/',
    desc: 'Robot controller backups and controller maps',
    icon: Bot
  },
  {
    id: 'mtconnect',
    path: 'MTConnect/',
    desc: 'Agent probes and registered DataItems',
    icon: Radio
  },
  // Kept for when ERP/MES ingest lands; hidden until then.
  {
    id: 'erp',
    path: 'ERP-MES/',
    desc: 'Production and order tables pulled on a schedule',
    icon: Database
  },
  {
    id: 'unstructured',
    path: 'Unstructured-Documents/',
    desc: 'P&ID drawings, manuals, SOPs, and reports',
    icon: Files
  }
]

const HIDDEN_FILE_GROUPS = new Set<IngestedFileGroupId>(['erp'])

const VISIBLE_FILE_GROUPS = FILE_GROUPS.filter((g) => !HIDDEN_FILE_GROUPS.has(g.id))

type ViewerTarget =
  | { mode: 'job'; jobId: string; path: string; note?: string; noteStatus?: string }
  | { mode: 'stored'; objectKey: string; fileName: string; note?: string }

interface DisplayFile {
  key: string
  fileName: string
  size?: number
  lastModified?: string
  statusLabel: string
  status: Status
  viewer?: ViewerTarget
}

interface IngestedFilesPanelProps {
  siteId: string
  siteName: string
}

function useKindDocs(siteId: string, siteName: string, kind: SiteDocumentKind | undefined) {
  const alsoSiteIds = React.useMemo(() => {
    const ids = alternateSiteStorageIds(siteId, siteName)
    return ids.slice(1)
  }, [siteId, siteName])

  return useListSiteDocumentsQuery(
    { siteId, kind: kind!, alsoSiteIds },
    { skip: !siteId || !kind, refetchOnMountOrArgChange: true }
  )
}

function storedArtifactLabel(groupId: IngestedFileGroupId): string {
  if (groupId === 'ipmap') return 'IP map'
  if (groupId === 'opctags') return 'OPC tag list'
  return groupId.toUpperCase()
}

function siteDocToDisplay(doc: SiteDocument, groupId: IngestedFileGroupId): DisplayFile {
  const objectKey = doc.key?.trim()
  return {
    key: objectKey || `s3:${doc.fileName}:${doc.lastModified}:${doc.size}`,
    fileName: doc.fileName,
    size: doc.size,
    lastModified: doc.lastModified,
    statusLabel: 'stored',
    status: 'run',
    viewer: objectKey
      ? {
          mode: 'stored',
          objectKey,
          fileName: doc.fileName,
          note:
            groupId === 'scada'
              ? 'Retained ProjectRoot snapshot for this site — not necessarily the original gateway backup upload.'
              : `Stored ${storedArtifactLabel(groupId)} artifact for this site.`
        }
      : undefined
  }
}

function unstructuredStatus(status: string): { label: string; status: Status } {
  switch (status) {
    case 'ingested':
      return { label: 'ingested', status: 'run' }
    case 'pending':
      return { label: 'pending', status: 'warn' }
    case 'skipped':
      return { label: 'skipped', status: 'idle' }
    case 'unsupported':
      return { label: 'unsupported', status: 'down' }
    default:
      return { label: status || 'stored', status: 'idle' }
  }
}

function displayFileDeleteArg(file: DisplayFile): DeleteIngestedFileArg | undefined {
  if (!file.viewer) return undefined
  if (file.viewer.mode === 'job') return { jobId: file.viewer.jobId, path: file.viewer.path }
  return { key: file.viewer.objectKey }
}

function jobFileToDisplay(file: SiteUnstructuredFile): DisplayFile {
  const tone = unstructuredStatus(file.status)
  return {
    key: `job:${file.jobId}:${file.path}`,
    fileName: file.fileName,
    lastModified: file.updatedAtMs > 0 ? new Date(file.updatedAtMs).toISOString() : undefined,
    statusLabel: tone.label,
    status: tone.status,
    viewer: {
      mode: 'job',
      jobId: file.jobId,
      path: file.path,
      note: 'Unstructured corpus file from the ingest job.',
      noteStatus: file.status
    }
  }
}

function mergeUnstructuredFiles(
  siteDocs: SiteDocument[],
  jobFiles: SiteUnstructuredFile[]
): DisplayFile[] {
  const out: DisplayFile[] = []
  const seenNames = new Set<string>()
  for (const file of jobFiles.map(jobFileToDisplay)) {
    out.push(file)
    seenNames.add(file.fileName.toLowerCase())
  }
  for (const doc of siteDocs) {
    // Prefer the richer job-inventory row when the same basename already landed.
    if (seenNames.has(doc.fileName.toLowerCase())) continue
    out.push(siteDocToDisplay(doc, 'unstructured'))
  }
  return out
}

export function IngestedFilesPanel({ siteId, siteName }: IngestedFilesPanelProps) {
  const alsoSiteIds = React.useMemo(() => {
    const ids = alternateSiteStorageIds(siteId, siteName)
    return ids.slice(1)
  }, [siteId, siteName])

  const scadaQ = useKindDocs(siteId, siteName, 'project-roots')
  const plcQ = useKindDocs(siteId, siteName, 'plc')
  const ipmapQ = useKindDocs(siteId, siteName, 'ip-map')
  const tagListsQ = useKindDocs(siteId, siteName, 'tag-lists')
  const docsQ = useKindDocs(siteId, siteName, 'documents')
  const unstructuredQ = useListSiteUnstructuredFilesQuery(
    { siteId, alsoSiteIds },
    { skip: !siteId, refetchOnMountOrArgChange: true }
  )
  const { openSource, viewerProps } = useSourceDocViewer()
  // Deleting stored files is destructive and bypasses the ingest lifecycle, so
  // it is a debug-only affordance until the reaper/inventory work settles.
  const debugMode = useDebugMode()
  const [pendingDelete, setPendingDelete] = React.useState<DisplayFile | null>(null)
  const [deleteIngestedFile, deleteState] = useDeleteIngestedFileMutation()
  const resetDelete = deleteState.reset

  const filesByGroup = React.useMemo(() => {
    const byStorage: Record<SiteDocumentKind, DisplayFile[]> = {
      'project-roots': (scadaQ.data ?? []).map((doc) => siteDocToDisplay(doc, 'scada')),
      plc: (plcQ.data ?? []).map((doc) => siteDocToDisplay(doc, 'plc')),
      'ip-map': (ipmapQ.data ?? []).map((doc) => siteDocToDisplay(doc, 'ipmap')),
      'tag-lists': (tagListsQ.data ?? []).map((doc) => siteDocToDisplay(doc, 'opctags')),
      documents: mergeUnstructuredFiles(docsQ.data ?? [], unstructuredQ.data ?? [])
    }
    return {
      scada: byStorage['project-roots'],
      plc: byStorage.plc,
      ipmap: byStorage['ip-map'],
      opctags: byStorage['tag-lists'],
      robotics: [] as DisplayFile[],
      mtconnect: [] as DisplayFile[],
      erp: [] as DisplayFile[],
      unstructured: byStorage.documents
    } satisfies Record<IngestedFileGroupId, DisplayFile[]>
  }, [scadaQ.data, plcQ.data, ipmapQ.data, tagListsQ.data, docsQ.data, unstructuredQ.data])

  const isLoading =
    scadaQ.isLoading ||
    plcQ.isLoading ||
    ipmapQ.isLoading ||
    tagListsQ.isLoading ||
    docsQ.isLoading ||
    unstructuredQ.isLoading
  const error =
    scadaQ.error ||
    plcQ.error ||
    ipmapQ.error ||
    tagListsQ.error ||
    docsQ.error ||
    unstructuredQ.error

  // Explicit overrides only. Unset → empty groups collapsed, populated open.
  const [closedOverride, setClosedOverride] = React.useState<
    Partial<Record<IngestedFileGroupId, boolean>>
  >({})

  const total = VISIBLE_FILE_GROUPS.reduce((sum, g) => sum + filesByGroup[g.id].length, 0)

  const openFile = (file: DisplayFile) => {
    if (!file.viewer) return
    if (file.viewer.mode === 'job') {
      openSource({
        jobId: file.viewer.jobId,
        sourceFile: file.viewer.path,
        note: file.viewer.note,
        noteStatus: file.viewer.noteStatus
      })
      return
    }
    const { objectKey, fileName, note } = file.viewer
    openSource({
      sourceFile: fileName,
      note,
      supportsOriginalDownload: false,
      loadSource: () => fetchSiteStoredDocument(objectKey)
    })
  }

  const confirmDelete = async () => {
    if (!pendingDelete) return
    const arg = displayFileDeleteArg(pendingDelete)
    if (!arg) return
    try {
      await deleteIngestedFile(arg).unwrap()
      setPendingDelete(null)
    } catch {
      // Dialog stays open and surfaces deleteState.isError.
    }
  }

  if (error) {
    return (
      <Card className="px-4 py-3 text-sm text-st-down">
        Failed to load ingested files for this site.
      </Card>
    )
  }

  if (isLoading) {
    return (
      <Card className="flex items-center gap-2 px-4 py-6 text-sm text-ink-3">
        <Loader2 className="size-4 animate-spin" />
        Loading ingested files…
      </Card>
    )
  }

  return (
    <>
      <Card className="gap-0 overflow-hidden py-0">
        <div className="flex items-center gap-2.5 border-b border-line px-4 py-2.5 text-11 font-medium tracking-[0.05em] text-ink-3 uppercase">
          <span className="flex-1">Source</span>
          <span className="font-mono text-11 tracking-normal normal-case tabular-nums">
            {total} file{total === 1 ? '' : 's'}
          </span>
        </div>

        {VISIBLE_FILE_GROUPS.map((group) => {
          const files = filesByGroup[group.id]
          const defaultOpen = files.length > 0
          const open =
            closedOverride[group.id] === undefined ? defaultOpen : !closedOverride[group.id]
          const Icon = group.icon
          return (
            <div key={group.id} className="border-b border-line last:border-b-0">
              <button
                type="button"
                onClick={() => setClosedOverride((prev) => ({ ...prev, [group.id]: open }))}
                aria-expanded={open}
                className={cn(
                  'flex w-full items-center gap-2.5 px-4 py-2.5 text-left transition-colors duration-150 motion-reduce:transition-none',
                  open ? 'bg-bg-elev' : 'bg-transparent hover:bg-bg-sunk/60'
                )}
              >
                <ChevronDown
                  className={cn(
                    'size-3.5 shrink-0 text-ink-3 transition-transform duration-200 ease-out motion-reduce:transition-none',
                    !open && '-rotate-90'
                  )}
                />
                <Icon className="size-3.5 shrink-0 text-ink-2" />
                <span className="shrink-0 font-mono text-13 text-ink">{group.path}</span>
                <span className="min-w-0 flex-1 truncate text-xs text-ink-3">{group.desc}</span>
                <span className="shrink-0 font-mono text-11 text-ink-3 tabular-nums">
                  {files.length}
                </span>
              </button>
              <div
                data-open={open}
                className="grid grid-rows-[0fr] transition-[grid-template-rows] duration-200 ease-out motion-reduce:transition-none data-[open=true]:grid-rows-[1fr]"
              >
                <div className="min-h-0 overflow-hidden">
                  <div
                    data-open={open}
                    className="translate-y-[-2px] border-t border-line bg-transparent opacity-0 transition-[opacity,transform] duration-200 ease-out motion-reduce:transition-none data-[open=true]:translate-y-0 data-[open=true]:opacity-100"
                  >
                    {files.length === 0 ? (
                      <div className="px-11 py-2 text-xs text-ink-3">
                        Nothing ingested for this source yet.
                      </div>
                    ) : (
                      files.map((file, index) => {
                        const interactive = !!file.viewer
                        const canDelete = debugMode && !!displayFileDeleteArg(file)
                        return (
                          <div
                            key={file.key}
                            className={cn(
                              'grid w-full items-center gap-3 px-4 py-1.5 pl-11',
                              debugMode
                                ? 'grid-cols-[minmax(0,1fr)_minmax(6.25rem,9.375rem)_6rem_5.25rem_5.75rem_2rem]'
                                : 'grid-cols-[minmax(0,1fr)_minmax(6.25rem,9.375rem)_6rem_5.25rem_5.75rem]',
                              index > 0 && 'border-t border-line',
                              interactive &&
                                'transition-colors duration-150 hover:bg-brand-soft/30 motion-reduce:transition-none'
                            )}
                          >
                            <button
                              type="button"
                              disabled={!interactive}
                              onClick={() => openFile(file)}
                              className={cn(
                                'flex min-w-0 items-center gap-1.5 text-left',
                                interactive
                                  ? 'cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/30'
                                  : 'cursor-default'
                              )}
                            >
                              <File className="size-3.5 shrink-0 text-ink-3" />
                              <span className="truncate font-mono text-xs text-ink">
                                {file.fileName}
                              </span>
                            </button>
                            <span className="truncate text-xs text-ink-2">{siteName}</span>
                            <span className="text-12 text-ink-3">
                              {typeof file.size === 'number' ? formatBytes(file.size) : '—'}
                            </span>
                            <span className="text-12 text-ink-3">
                              {file.lastModified ? formatRelativeTime(file.lastModified) : '—'}
                            </span>
                            <span className="flex justify-end">
                              <StatusPill status={file.status}>{file.statusLabel}</StatusPill>
                            </span>
                            {!debugMode ? null : canDelete ? (
                              <button
                                type="button"
                                aria-label={`Delete ${file.fileName}`}
                                onClick={() => setPendingDelete(file)}
                                className="flex size-7 items-center justify-center rounded text-ink-3 hover:bg-bg-sunk hover:text-st-down"
                              >
                                <Trash2 className="size-3.5" />
                              </button>
                            ) : (
                              <span />
                            )}
                          </div>
                        )
                      })
                    )}
                  </div>
                </div>
              </div>
            </div>
          )
        })}

        <div className="px-4 py-2.5 text-xs text-ink-3">
          Everything ingested for {siteName}, grouped by source. Sorted newest first within each
          group when the store returns it that way.
        </div>
      </Card>
      <SourceDocViewer {...viewerProps} />
      <IngestFileDeleteDialog
        fileName={pendingDelete?.fileName ?? ''}
        open={pendingDelete !== null}
        onOpenChange={(open) => {
          if (!open) {
            setPendingDelete(null)
            resetDelete()
          }
        }}
        onConfirm={() => {
          void confirmDelete()
        }}
        isDeleting={deleteState.isLoading}
        error={deleteState.isError}
      />
    </>
  )
}
